import { useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { Box, Link, Stack, Tabs, Text } from '@ekhodealer/ekho-common/rev';

import { SettingsSection } from '@common/components/SettingsSection';
import { SettingsPageContent, type TocItem } from '@common/components/SettingsTableOfContents';
import { useCurrentServicesProviderId } from '@common/hooks/useCurrentServicesProviderId';
import { paths } from '@config/paths';
import { SettingsSection as SettingsSectionPath } from '@config/paths/settingsSections';

import { useGetMacroInformation } from '../api/useGetMacroInformation';

import { AppearanceSection } from './AppearanceSection';
import { SalesAppointmentsSection, ServiceAppointmentsSection } from './BookingSection';
import { ChannelsSection } from './ChannelsSection';
import { ChatExperienceSection } from './ChatExperienceSection';
import {
	AboutSection,
	BlogSection,
	BrandsSection,
	EventsSection,
	FinancingSection,
	PoliciesSection,
	PromotionsSection,
	ServicesSection,
	TeamSection,
	TestimonialsSection,
} from './ContextSection';
import { CrmReferenceSummary } from './CrmReferenceSummary';
import { DealerHoursSection } from './DealerHoursSection';
import { FollowUpSection } from './FollowUpSection';
import { IdentitySection } from './IdentitySection';
import { PreQualificationSection } from './PreQualificationSection';
import { ResponseHandlingSection } from './ResponseHandlingSection';
import { ShippingFeesSection } from './ShippingFeesSection';
import { SmsSettingsSection } from './SmsSettingsSection';
import { TradeInValuationSection } from './TradeInValuationSection';

import './AiBdcSettingsPage.scss';


type AiBdcTab = 'general' | 'skills' | 'website-assistant' | 'sms' | 'context';

const TAB_ITEMS: { id: AiBdcTab; content: string }[] = [
	{ id: 'general', content: 'General' },
	{ id: 'skills', content: 'Skills' },
	{ id: 'website-assistant', content: 'Website Assistant' },
	{ id: 'sms', content: 'SMS' },
	{ id: 'context', content: 'Context' },
];

// --- TOC items per tab ---

const GENERAL_TOC: TocItem[] = [
	{ id: 'channels', title: 'Channels' },
	{ id: 'identity', title: 'Identity' },
	{ id: 'follow-up', title: 'Follow-up' },
	{ id: 'crm-forwarding', title: 'CRM forwarding' },
	{ id: 'business-hours', title: 'Business hours' },
];

const SKILLS_TOC: TocItem[] = [
	{ id: 'escalation', title: 'Escalation' },
	{ id: 'sales-appointments', title: 'Sales appointments' },
	{ id: 'service-appointments', title: 'Service appointments' },
	{ id: 'pre-qualification', title: 'Pre-qualification' },
	{ id: 'trade-in-valuation', title: 'Trade-in valuation' },
	{ id: 'shipping-fees', title: 'Shipping fees' },
];

const WEBSITE_ASSISTANT_TOC: TocItem[] = [
	{ id: 'appearance', title: 'Appearance' },
	{ id: 'chat-widget', title: 'Chat widget' },
];

const SMS_TOC: TocItem[] = [
	{ id: 'overview', title: 'Overview' },
];

const CONTEXT_TOC: TocItem[] = [
	{ id: 'inventory', title: 'Inventory' },
	{ id: 'about', title: 'About' },
	{ id: 'team', title: 'Team' },
	{ id: 'brands-product-lines', title: 'Brands & product lines' },
	{ id: 'promotions-specials', title: 'Promotions & specials' },
	{ id: 'events-calendar', title: 'Events & calendar' },
	{ id: 'service-parts', title: 'Service & parts' },
	{ id: 'financing-insurance', title: 'Financing & insurance' },
	{ id: 'reviews-testimonials', title: 'Reviews & testimonials' },
	{ id: 'blog-articles', title: 'Blog & articles' },
	{ id: 'policies-faqs', title: 'Policies & FAQs' },
];

function GeneralTab() {
	return (
		<SettingsPageContent tocItems={GENERAL_TOC} stickyTop={64}>
			<SettingsSection
				title='Channels'
				description='Choose how your AI assistant reaches customers — on your website, via text, via email, or any combination.'>
				<ChannelsSection />
			</SettingsSection>
			<SettingsSection
				title='Identity'
				description="Define your AI assistant's name, language, and personality.">
				<IdentitySection />
			</SettingsSection>
			<SettingsSection
				title='Follow-up'
				description='Automatically re-engage leads who stop responding via SMS or email. The website assistant does not send follow-ups.'>
				<FollowUpSection />
			</SettingsSection>
			<SettingsSection
				title='CRM forwarding'
				description='View CRM forwarding configuration. Managed in Integrations settings.'>
				<CrmReferenceSummary />
			</SettingsSection>
			<SettingsSection
				title='Business hours'
				description='View the business hours your AI references when talking to leads.'
				hideDivider>
				<DealerHoursSection />
			</SettingsSection>
		</SettingsPageContent>
	);
}

function SkillsTab() {
	return (
		<SettingsPageContent tocItems={SKILLS_TOC} stickyTop={64}>
			<SettingsSection
				title='Escalation'
				description='Control how the AI hands off conversations when it needs human support.'>
				<ResponseHandlingSection />
			</SettingsSection>
			<SettingsSection
				title='Sales appointments'
				description='Configure which sales appointment types the AI can offer. Appointments are always added to your Ekho scheduling calendar. Optionally, connect an external calendar to check availability and sync bookings.'>
				<SalesAppointmentsSection />
			</SettingsSection>
			<SettingsSection
				title='Service appointments'
				description='Configure which service appointment types the AI can offer. Appointments are always added to your Ekho scheduling calendar. Optionally, connect an external calendar to check availability and sync bookings.'>
				<ServiceAppointmentsSection />
			</SettingsSection>
			<SettingsSection
				title='Pre-qualification'
				description='Let the AI gather buyer readiness info before handing off to your sales team.'>
				<PreQualificationSection />
			</SettingsSection>
			<SettingsSection
				title='Trade-in valuation'
				description='Enable the AI to collect vehicle details and provide trade-in estimates.'>
				<TradeInValuationSection />
			</SettingsSection>
			<SettingsSection
				title='Shipping fees'
				description="Let the AI estimate delivery costs based on the buyer's location."
				hideDivider>
				<ShippingFeesSection />
			</SettingsSection>
		</SettingsPageContent>
	);
}

function WebsiteAssistantTab() {
	return (
		<SettingsPageContent tocItems={WEBSITE_ASSISTANT_TOC} stickyTop={64}>
			<SettingsSection
				title='Appearance'
				description='Customize the look and feel of your chat widget to match your brand.'>
				<AppearanceSection />
			</SettingsSection>
			<SettingsSection
				title='Chat widget'
				description='Customize the chat experience — starter prompts, quick actions, and interactive features.'
				hideDivider>
				<ChatExperienceSection />
			</SettingsSection>
		</SettingsPageContent>
	);
}

function InventoryReferenceSummary(): React.JSX.Element {
	const navigate = useNavigate();
	const spId = useCurrentServicesProviderId();

	return (
		<Box padding='24' background='secondary' rounding='12'>
			<Stack itemsSpacing='12'>
				<Text size='body' weight='semibold'>
					Inventory source settings
				</Text>
				<Text size='bodySmall' color='secondary'>
					Inventory tracking is configured in your Inventory settings. Those settings define which inventory sources the AI agent uses to answer questions about your vehicles, pricing, and availability.
				</Text>
				<Link
					onPress={() =>
						navigate(
							paths.settingsSection.getHref(
								spId,
								SettingsSectionPath.SharedInventory,
							),
						)
					}>
					<Text size='bodySmall'>Edit in Inventory settings</Text>
				</Link>
			</Stack>
		</Box>
	);
}

function ContextTab() {
	const spId = useCurrentServicesProviderId();
	const { data: macroInfo } = useGetMacroInformation(spId);

	return (
		<SettingsPageContent tocItems={CONTEXT_TOC} stickyTop={64}>
			<SettingsSection
				title='Inventory'
				description='How the AI knows about your vehicles and pricing.'>
				<InventoryReferenceSummary />
			</SettingsSection>
			<SettingsSection
				title='About'
				description='Your business identity — who you are, where you are, and what makes you different.'>
				<AboutSection macroInfo={macroInfo} />
			</SettingsSection>
			<SettingsSection
				title='Team'
				description='The people behind your business.'>
				<TeamSection macroInfo={macroInfo} />
			</SettingsSection>
			<SettingsSection
				title='Brands & product lines'
				description='The manufacturers and product categories you carry.'>
				<BrandsSection />
			</SettingsSection>
			<SettingsSection
				title='Promotions & specials'
				description='Active deals, incentives, and limited-time offers.'>
				<PromotionsSection />
			</SettingsSection>
			<SettingsSection
				title='Events & calendar'
				description='Upcoming events, demos, and community gatherings.'>
				<EventsSection macroInfo={macroInfo} />
			</SettingsSection>
			<SettingsSection
				title='Service & parts'
				description='Service capabilities, parts availability, and maintenance programs.'>
				<ServicesSection macroInfo={macroInfo} />
			</SettingsSection>
			<SettingsSection
				title='Financing & insurance'
				description='Financing programs, credit options, and protection products.'>
				<FinancingSection />
			</SettingsSection>
			<SettingsSection
				title='Reviews & testimonials'
				description='Customer reviews and success stories from your website.'>
				<TestimonialsSection macroInfo={macroInfo} />
			</SettingsSection>
			<SettingsSection
				title='Blog & articles'
				description='Educational content, news, and lifestyle articles.'>
				<BlogSection />
			</SettingsSection>
			<SettingsSection
				title='Policies & FAQs'
				description='Return policies, warranties, and common questions.'
				hideDivider>
				<PoliciesSection macroInfo={macroInfo} />
			</SettingsSection>
		</SettingsPageContent>
	);
}

function SmsTab() {
	return (
		<SettingsPageContent tocItems={SMS_TOC} stickyTop={64}>
			<SettingsSection
				title='Overview'
				description='A summary of settings that apply to your SMS assistant. Most settings are managed in the General tab.'
				hideDivider>
				<SmsSettingsSection />
			</SettingsSection>
		</SettingsPageContent>
	);
}

export function AiBdcSettingsPage() {
	const [activeTab, setActiveTab] = useState<AiBdcTab>('general');

	return (
		<div className='ai-bdc-settings'>
			<div className='ai-bdc-settings__tabs' data-settings-tabs>
				<Tabs
					items={TAB_ITEMS}
					selectedKey={activeTab}
					onSelect={(item) => setActiveTab(item.id as AiBdcTab)}
				/>
			</div>
			<div className='ai-bdc-settings__content' data-settings-tab-content>
				{activeTab === 'general' && <GeneralTab />}
				{activeTab === 'skills' && <SkillsTab />}
				{activeTab === 'website-assistant' && <WebsiteAssistantTab />}
				{activeTab === 'sms' && <SmsTab />}
				{activeTab === 'context' && <ContextTab />}
			</div>
		</div>
	);
}
